import { db } from "@/lib/instantdb";

/**
 * User roles for access control
 * - user: Default role for all authenticated users
 * - analyst: Can view analytics dashboards
 * - admin: Full access to admin panel, gateway, and analytics
 */
export type UserRole = "user" | "analyst" | "admin";

/**
 * Route access requirements
 */
export interface RouteAccess {
  /** Roles that can access this route (any match grants access) */
  roles: UserRole[];
  /** If true, route is hidden on mobile */
  desktopOnly?: boolean;
}

/**
 * Route configuration for protected sections
 */
export const routeAccess: Record<string, RouteAccess> = {
  "/admin": { roles: ["admin"], desktopOnly: true },
  "/gateway": { roles: ["admin"], desktopOnly: true },
  "/analytics": { roles: ["admin", "analyst"], desktopOnly: true },
};

/**
 * Admin email domains that grant admin role automatically
 * Users with emails from these domains get admin access
 */
const ADMIN_DOMAINS = ["vizi.app", "vizi.video", "vizideveloper.com"];

/**
 * Specific admin emails (for external admins)
 * Configure via VITE_ADMIN_EMAILS environment variable (comma-separated)
 */
const ADMIN_EMAILS: string[] = (import.meta.env.VITE_ADMIN_EMAILS || "")
  .split(",")
  .map((email: string) => email.trim().toLowerCase())
  .filter(Boolean);

/**
 * Check if an email has admin privileges
 */
export function isAdminEmail(email: string | undefined): boolean {
  if (!email) return false;

  const emailLower = email.toLowerCase();

  // Check admin domains
  if (ADMIN_DOMAINS.some((domain) => emailLower.endsWith(`@${domain}`))) {
    return true;
  }

  // Check specific admin emails
  if (ADMIN_EMAILS.includes(emailLower)) {
    return true;
  }

  return false;
}

/**
 * Get user roles based on their email
 * In the future, this could query InstantDB for stored roles
 */
export function getUserRoles(email: string | undefined): UserRole[] {
  const roles: UserRole[] = ["user"]; // Everyone starts with user role

  if (isAdminEmail(email)) {
    roles.push("admin", "analyst");
  }

  return roles;
}

/**
 * Check if user has any of the required roles
 */
export function hasRole(
  userRoles: UserRole[],
  requiredRoles: UserRole[],
): boolean {
  return requiredRoles.some((role) => userRoles.includes(role));
}

/**
 * Check if user can access a specific route
 */
export function canAccessRoute(userRoles: UserRole[], path: string): boolean {
  // Find matching route config
  const matchingPath = Object.keys(routeAccess).find((routePath) =>
    path.startsWith(routePath),
  );

  if (!matchingPath) {
    // No specific access config = accessible to all authenticated users
    return true;
  }

  const access = routeAccess[matchingPath];
  return hasRole(userRoles, access.roles);
}

/**
 * Check if route should be hidden on mobile
 */
export function isDesktopOnly(path: string): boolean {
  const matchingPath = Object.keys(routeAccess).find((routePath) =>
    path.startsWith(routePath),
  );

  if (!matchingPath) return false;
  return routeAccess[matchingPath].desktopOnly ?? false;
}

/**
 * Hook to get current user's roles
 */
export function useUserRoles(): {
  roles: UserRole[];
  isLoading: boolean;
  isAdmin: boolean;
  isAnalyst: boolean;
} {
  const { isLoading, user } = db.useAuth();

  if (isLoading || !user) {
    return { roles: [], isLoading, isAdmin: false, isAnalyst: false };
  }

  const roles = getUserRoles(user.email);

  return {
    roles,
    isLoading: false,
    isAdmin: roles.includes("admin"),
    isAnalyst: roles.includes("analyst"),
  };
}

/**
 * Hook to check if user can access a specific route
 */
export function useCanAccessRoute(path: string): {
  canAccess: boolean;
  isLoading: boolean;
} {
  const { roles, isLoading } = useUserRoles();

  if (isLoading) {
    return { canAccess: false, isLoading: true };
  }

  return { canAccess: canAccessRoute(roles, path), isLoading: false };
}
